/* Herbruikbare vóór/na-sleepvergelijking. Werkt op elk element met [data-sf-slider]
   via Pointer Events, dus muis en touch lopen door dezelfde code. De hint-animatie
   (die de balk even heen en weer laat bewegen) gebruikt dezelfde setPos()-functie via
   requestAnimationFrame, in plaats van een losse CSS-animatie op dezelfde variabele —
   zo kunnen de hint en het echte slepen elkaar nooit in de weg zitten. */
(function () {
  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function posFromClientX(el, clientX) {
    var rect = el.getBoundingClientRect();
    if (rect.width === 0) return 0;
    return clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
  }

  function setPos(el, handle, pct) {
    pct = clamp(pct, 0, 100);
    el.style.setProperty('--sf-slider-pos', pct + '%');
    if (handle) handle.setAttribute('aria-valuenow', String(Math.round(pct)));
  }

  function easeInOutQuad(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  /* Speelt eenmalig een korte heen-en-weer beweging, dan terug naar 50%.
     Geeft een cancel()-functie terug zodat een echte sleepbeweging 'm meteen kan afbreken. */
  function createHint(el, handle) {
    var keyframes = [50, 28, 72, 40, 60, 50];
    var segmentMs = 340;
    var cancelled = false;
    var rafId = null;

    function cancel() {
      cancelled = true;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    function play() {
      if (cancelled) return;
      var totalMs = segmentMs * (keyframes.length - 1);
      var start = null;

      function step(ts) {
        if (cancelled) return;
        if (start === null) start = ts;
        var elapsed = ts - start;
        if (elapsed >= totalMs) {
          setPos(el, handle, 50);
          rafId = null;
          return;
        }
        var segIndex = Math.min(Math.floor(elapsed / segmentMs), keyframes.length - 2);
        var segProgress = (elapsed - segIndex * segmentMs) / segmentMs;
        var from = keyframes[segIndex];
        var to = keyframes[segIndex + 1];
        setPos(el, handle, from + (to - from) * easeInOutQuad(segProgress));
        rafId = requestAnimationFrame(step);
      }
      rafId = requestAnimationFrame(step);
    }

    return { play: play, cancel: cancel };
  }

  function initHint(el, handle) {
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return function () {};

    var hint = createHint(el, handle);
    var played = false;

    function trigger() {
      if (played) return;
      played = true;
      hint.play();
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) {
          trigger();
          io.disconnect();
        }
      }, { threshold: 0.4 });
      io.observe(el);
    } else {
      trigger();
    }

    return hint.cancel;
  }

  /* Op mobiel staan de projectkaarten in een horizontaal scrollende rij (peek-carrousel).
     Zonder deze check kaapt de sleepbalk elke aanraking op de foto, ook wanneer de kaart
     nog maar half in beeld is — waardoor doorswipen naar de volgende kaart niet lukt.
     Daarom staat slepen op mobiel pas 'aan' zodra de kaart volledig zichtbaar is in de
     rij; op desktop (geen horizontale scroll van de rij) blijft slepen altijd beschikbaar. */
  function initSwipeGate(el) {
    var scrollRoot = el.closest('.sf-cp-grid');
    if (!scrollRoot || !('IntersectionObserver' in window) || !window.matchMedia) {
      return function isFullyVisible() { return true; };
    }
    var mobileQuery = window.matchMedia('(max-width: 749px)');
    var fullyVisible = true;
    var io = null;

    function update() {
      var restrict = mobileQuery.matches;
      if (restrict && !io) {
        fullyVisible = false;
        el.classList.add('is-swipeable');
        io = new IntersectionObserver(function (entries) {
          fullyVisible = entries[0].intersectionRatio > 0.95;
          el.classList.toggle('is-swipeable', !fullyVisible);
        }, { root: scrollRoot, threshold: [0, 0.95, 1] });
        io.observe(el);
      } else if (!restrict && io) {
        io.disconnect();
        io = null;
        fullyVisible = true;
        el.classList.remove('is-swipeable');
      }
    }
    update();
    if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', update);
    else if (mobileQuery.addListener) mobileQuery.addListener(update);

    return function isFullyVisible() { return fullyVisible; };
  }

  function init(el) {
    var handle = el.querySelector('[data-sf-slider-handle]');
    var dragging = false;
    var cancelHint = initHint(el, handle);
    var isFullyVisible = initSwipeGate(el);

    function move(clientX) {
      setPos(el, handle, posFromClientX(el, clientX));
    }

    el.addEventListener('pointerdown', function (e) {
      if (!isFullyVisible()) return;
      cancelHint();
      dragging = true;
      el.classList.add('is-dragging');
      if (el.setPointerCapture) {
        try { el.setPointerCapture(e.pointerId); } catch (err) {}
      }
      move(e.clientX);
      e.preventDefault();
    });
    el.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      move(e.clientX);
    });
    function stop() {
      dragging = false;
      el.classList.remove('is-dragging');
    }
    el.addEventListener('pointerup', stop);
    el.addEventListener('pointercancel', stop);

    if (handle) {
      handle.addEventListener('focus', cancelHint);
      handle.addEventListener('keydown', function (e) {
        cancelHint();
        var current = parseFloat(getComputedStyle(el).getPropertyValue('--sf-slider-pos')) || 0;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          setPos(el, handle, current - 5);
          e.preventDefault();
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          setPos(el, handle, current + 5);
          e.preventDefault();
        } else if (e.key === 'Home') {
          setPos(el, handle, 0);
          e.preventDefault();
        } else if (e.key === 'End') {
          setPos(el, handle, 100);
          e.preventDefault();
        }
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-sf-slider]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
